/* eslint-disable formatjs/no-literal-string-in-jsx */
import React, { useState, useMemo } from 'react';
import { SelectInline, Option } from '@mc/wink';
import stylesheet from './InputPhone.css';
import { getCountryData } from './input-phone-helpers';

// Type definitions
type CountryOption = {
  value: string;
  label: string;
  displayLabel: string;
  flag: string;
  code: string;
};

type RawCountry = {
  name: string;
  value?: string;
  code?: string;
  dialingCode?: string;
};

// Special cases for territories that should use parent country flags (cached outside function)
const FLAG_SPECIAL_CASES: Record<string, string> = {
  sj: '🇳🇴', // Svalbard and Jan Mayen -> Norway
  bv: '🇳🇴', // Bouvet Island -> Norway
  an: '🇳🇱', // Netherlands Antilles -> Netherlands
};

// Function to get flag emoji using Unicode generation
function getFlagEmoji(countryCode: string) {
  if (!countryCode) {
    return '🇺🇸'; // Default to US flag
  }

  const code = countryCode.toLowerCase();

  if (FLAG_SPECIAL_CASES[code]) {
    return FLAG_SPECIAL_CASES[code];
  }

  // Generate flag emoji using Unicode
  try {
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map((char) => 0x1f1e6 - 65 + char.charCodeAt(0));
    const flag = String.fromCodePoint(...codePoints);

    // Validate that we got a proper flag (not replacement characters)
    if (flag && flag !== '' && flag.length >= 2) {
      return flag;
    }
  } catch (error) {
    // Log errors in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Error generating flag for country code: "${code}"`, error);
    }
  }

  // Final fallback - use a neutral flag emoji
  return '🏁'; // Checkered flag as neutral fallback
}

type CountrySelectorProps = {
  value?: string;
  onChange?: (country: CountryOption) => void;
  onFocus?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  disabled?: boolean;
  className?: string;
  helpText?: string;
  error?: string;
  readOnly?: boolean;
  onClose?: () => void;
  searchable?: boolean;
  forwardedRef?: React.Ref<HTMLDivElement> | null;
};

export const CountrySelector: React.FC<CountrySelectorProps> = ({
  error = '',
  helpText = '',
  className = '',
  onChange: externalOnChange,
  value: externalValue,
  readOnly = false,
  disabled = false,
  searchable = true,
  onClose = () => {},
  onFocus = () => {},
  forwardedRef = null,
}) => {
  const [selected, setSelected] = useState(externalValue || 'us');
  const countries = getCountryData();

  // Convert countries data to our format with flags and dialing codes
  const countryOptions = useMemo(() => {
    if (!countries || !Array.isArray(countries)) {
      return [];
    }

    return (countries as RawCountry[])
      .filter(
        (country: RawCountry) =>
          country.name && country.name !== 'Please select a country',
      )
      .map((country: RawCountry) => {
        // Use the unified data structure - country already has code and dialingCode
        const countryCode =
          country.code || country.value?.toLowerCase() || 'us';
        const dialingCode = country.dialingCode || '+1';

        // Generate flag emoji
        const flag = getFlagEmoji(countryCode);

        // Log problematic countries in development
        if (process.env.NODE_ENV === 'development' && flag === '🏁') {
          console.warn(
            `No flag mapping found for country: "${country.name}" (code: "${countryCode}")`,
          );
        }

        return {
          value: countryCode,
          label: `${country.name} ${dialingCode} ${countryCode.toUpperCase()}`,
          displayLabel: country.name,
          flag: flag,
          code: dialingCode,
        };
      })
      .sort((a: CountryOption, b: CountryOption) => {
        // Put US first, then sort alphabetically
        if (a.value === 'us') {
          return -1;
        }
        if (b.value === 'us') {
          return 1;
        }
        return a.label.localeCompare(b.label);
      });
  }, [countries]);

  const handleChange = (val: string | React.ReactNode) => {
    const stringVal = String(val);
    setSelected(stringVal);

    // Find the complete country object and pass it to the parent
    const selectedCountryData = countryOptions.find(
      (c: CountryOption) => c.value === stringVal,
    );

    if (externalOnChange && selectedCountryData) {
      // Only pass complete country objects
      externalOnChange(selectedCountryData);
    }

    // When an option is selected, trigger close event to transfer focus
    if (onClose) {
      onClose();
    }
  };

  const handleSelectFocus = (event: React.FocusEvent<HTMLElement>) => {
    if (onFocus) {
      // Cast the event to the expected type and call onFocus
      onFocus(event as React.FocusEvent<HTMLSelectElement>);
    }
  };

  const selectedCountry = countryOptions.find(
    (c: CountryOption) => c.value === selected,
  );
  const usCountry = countryOptions.find((c: CountryOption) => c.value === 'us');
  const displayCountry = selectedCountry || usCountry || countryOptions[0];

  // For read-only mode, render flag + code without dropdown
  if (readOnly) {
    return (
      <div
        className={`${stylesheet.countrySelector} ${stylesheet.readOnly} ${className}`}
      >
        <span className={stylesheet.readOnlyDisplay}>
          <span
            className={stylesheet.selectedFlag}
            role="img"
            aria-label={`${
              displayCountry?.displayLabel || displayCountry?.label
            } flag`}
          >
            {displayCountry?.flag}
          </span>
          <span className={stylesheet.countryCode}>{displayCountry?.code}</span>
        </span>
      </div>
    );
  }

  return (
    <div
      className={`${stylesheet.countrySelector} ${className}`}
      ref={forwardedRef}
    >
      <SelectInline
        className={stylesheet.countrySelectInline}
        label="Country"
        hideLabel={true}
        // @ts-expect-error Wink's SelectInline has a complex onChange type that we are overriding.
        onChange={handleChange}
        placeholder="Search"
        value={selected}
        disabled={disabled}
        searchable={searchable}
        data-testid="country-select-inline"
        data-component="input-phone-country-selector"
        onFocus={handleSelectFocus}
        renderSelectedValue={(selectedValues) => {
          if (
            selectedValues &&
            Array.isArray(selectedValues) &&
            selectedValues.length > 0
          ) {
            const firstValue = selectedValues[0];
            const valueString =
              typeof firstValue === 'object' &&
              firstValue !== null &&
              'value' in firstValue
                ? (firstValue as { value: string }).value
                : String(firstValue);
            const selectedCountryData = countryOptions.find(
              (c: CountryOption) => c.value === valueString,
            );
            if (selectedCountryData) {
              return (
                <span
                  className={stylesheet.selectedFlag}
                  role="img"
                  aria-label={`${
                    selectedCountryData.displayLabel ||
                    selectedCountryData.label
                  } flag`}
                >
                  {selectedCountryData.flag}
                </span>
              );
            }
          }
          // Fallback to display country if no selection
          return (
            displayCountry && (
              <span
                className={stylesheet.selectedFlag}
                role="img"
                aria-label={`${
                  displayCountry.displayLabel || displayCountry.label
                } flag`}
              >
                {displayCountry.flag}
              </span>
            )
          );
        }}
      >
        {countryOptions.map((country: CountryOption) => (
          <Option
            key={country.value}
            value={country.value}
            label={country.label}
            hideCheckbox={true}
            data-testid={`option-${country.value}`}
          >
            <div className={stylesheet.countryOption}>
              <span
                className={stylesheet.flagIcon}
                role="img"
                aria-label={`${country.displayLabel || country.label} flag`}
              >
                {country.flag}
              </span>
              <span className={stylesheet.countryLabel}>
                {country.displayLabel || country.label}
              </span>
              <span className={stylesheet.dialingCodeBadge}>
                {country.code}
              </span>
            </div>
          </Option>
        ))}
      </SelectInline>
      {helpText && (
        <div className={stylesheet.helpText} data-testid="help-text">
          {helpText}
        </div>
      )}
      {error && (
        <div className={stylesheet.errorText} data-testid="error-text">
          {error}
        </div>
      )}
    </div>
  );
};

// Set displayName for Storybook
CountrySelector.displayName = 'CountrySelector';

export type { CountryOption, CountrySelectorProps };
export default CountrySelector;
